// WebAuthn JSON ⇄ browser API (RBAC Phase 4). auth-server sends and verifies the SimpleWebAuthn
// JSON shapes (base64url strings); `navigator.credentials` takes and returns ArrayBuffers. These
// helpers convert between them without a dependency (PublicKeyCredential.parse*FromJSON is not in
// every browser the console supports).

export function bufferToBase64Url(buffer: ArrayBuffer | ArrayBufferView): string {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out.buffer;
}

type CredentialDescriptorJSON = { id: string; type: 'public-key'; transports?: string[] };

export type RequestOptionsJSON = {
  challenge: string;
  rpId?: string;
  timeout?: number;
  allowCredentials?: CredentialDescriptorJSON[];
  userVerification?: UserVerificationRequirement;
  extensions?: AuthenticationExtensionsClientInputs;
};

export type CreationOptionsJSON = {
  challenge: string;
  rp: PublicKeyCredentialRpEntity;
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: CredentialDescriptorJSON[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
  extensions?: AuthenticationExtensionsClientInputs;
};

const descriptor = (d: CredentialDescriptorJSON): PublicKeyCredentialDescriptor => ({
  id: base64UrlToBuffer(d.id),
  type: d.type,
  ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
});

export function toRequestOptions(json: RequestOptionsJSON): PublicKeyCredentialRequestOptions {
  const { allowCredentials, challenge, ...rest } = json;
  return {
    ...rest,
    challenge: base64UrlToBuffer(challenge),
    ...(allowCredentials ? { allowCredentials: allowCredentials.map(descriptor) } : {}),
  };
}

export function toCreationOptions(json: CreationOptionsJSON): PublicKeyCredentialCreationOptions {
  const { excludeCredentials, challenge, user, ...rest } = json;
  return {
    ...rest,
    challenge: base64UrlToBuffer(challenge),
    user: { ...user, id: base64UrlToBuffer(user.id) },
    ...(excludeCredentials ? { excludeCredentials: excludeCredentials.map(descriptor) } : {}),
  };
}

type AssertionLike = {
  id: string;
  rawId: ArrayBuffer;
  type: string;
  authenticatorAttachment?: string | null;
  response: { clientDataJSON: ArrayBuffer; authenticatorData: ArrayBuffer; signature: ArrayBuffer; userHandle: ArrayBuffer | null };
  getClientExtensionResults: () => AuthenticationExtensionsClientOutputs;
};

type AttestationLike = {
  id: string;
  rawId: ArrayBuffer;
  type: string;
  authenticatorAttachment?: string | null;
  response: { clientDataJSON: ArrayBuffer; attestationObject: ArrayBuffer; getTransports?: () => string[] };
  getClientExtensionResults: () => AuthenticationExtensionsClientOutputs;
};

/** `navigator.credentials.get()` result → AuthenticationResponseJSON. */
export function serializeAssertion(cred: AssertionLike) {
  return {
    id: cred.id,
    rawId: bufferToBase64Url(cred.rawId),
    type: cred.type,
    ...(cred.authenticatorAttachment ? { authenticatorAttachment: cred.authenticatorAttachment } : {}),
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64Url(cred.response.clientDataJSON),
      authenticatorData: bufferToBase64Url(cred.response.authenticatorData),
      signature: bufferToBase64Url(cred.response.signature),
      ...(cred.response.userHandle ? { userHandle: bufferToBase64Url(cred.response.userHandle) } : {}),
    },
  };
}

/** `navigator.credentials.create()` result → RegistrationResponseJSON. */
export function serializeAttestation(cred: AttestationLike) {
  const transports = typeof cred.response.getTransports === 'function' ? cred.response.getTransports() : undefined;
  return {
    id: cred.id,
    rawId: bufferToBase64Url(cred.rawId),
    type: cred.type,
    ...(cred.authenticatorAttachment ? { authenticatorAttachment: cred.authenticatorAttachment } : {}),
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64Url(cred.response.clientDataJSON),
      attestationObject: bufferToBase64Url(cred.response.attestationObject),
      ...(transports ? { transports } : {}),
    },
  };
}

/** True when this browser can run WebAuthn ceremonies at all. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials;
}
